import React from 'react'

import __uniqueId from 'lodash/uniqueId'
import styled, { css } from 'styled-components'

import { ADVANCED_ACCOUNTING_CLASS_NAME } from '@constants/common'

import { getInputBorderColor } from './helpers'
import type { InputProps } from './types'

export const InputWrapper = styled.div<{
  $dense?: boolean
  $error?: boolean
  $hasAdornment?: boolean
  $disabled?: boolean
  $highlighted?: boolean
}>`
  --input-advanced-highlighted-border-color: ${({ $disabled, $error, $highlighted, theme }) =>
    getInputBorderColor({
      $disabled,
      $error,
      theme,
      $highlighted,
      $isAdvancedAccounting: true,
    })};
  --input-border-color: ${({ $disabled, $error, $highlighted, theme }) =>
    getInputBorderColor({ $disabled, $error, theme, $highlighted })};

  --input-vertical-padding: ${({ $dense }) => ($dense ? '4px' : '6px')};
  --input-horizontal-padding: ${({ $dense }) => ($dense ? '8px' : '10px')};
  --input-height: ${({ $dense }) => ($dense ? 32 : 36)}px;
  --input-focus-border-color: ${({ theme }) => theme.colors.common.outlineBlue};

  --input-disabled-bg-color: ${({ theme }) => theme.colors.field.disabledBackground};
  --input-bg-color: ${({ theme }) => theme.colors.field.background};

  display: flex;
  align-items: center;
  width: 100%;
  height: auto;
  line-height: 1.3;

  ${({ $hasAdornment, $disabled }) =>
    $hasAdornment
      ? css`
          gap: 4px;
          padding: 0 var(--input-horizontal-padding);
          border-width: 1px;
          border-style: solid;
          border-color: var(--input-border-color);
          border-radius: 4px;
          background-color: ${$disabled ? 'var(--input-disabled-bg-color)' : 'var(--input-bg-color)'};

          :focus-within,
          :hover {
            border-color: var(--input-focus-border-color);
          }

          input {
            border: none;
            background-color: transparent;
            padding: 0;
            height: calc(var(--input-height) - 2px);
          }
        `
      : css`
          padding: 0;

          input {
            border-width: 1px;
            border-style: solid;
            border-color: var(--input-border-color);
            border-radius: 4px;
            background-color: var(--input-bg-color);
            padding: var(--input-vertical-padding) var(--input-horizontal-padding);
            height: var(--input-height);

            :focus {
              border-color: var(--input-focus-border-color);
            }
            :disabled {
              background-color: var(--input-disabled-bg-color);
            }
          }
        `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: var(--input-disabled-bg-color);
    `}

  ${({ $highlighted, $hasAdornment }) =>
    $highlighted &&
    ($hasAdornment
      ? css`
          .${ADVANCED_ACCOUNTING_CLASS_NAME} & {
            border-color: var(--input-advanced-highlighted-border-color);

            :focus-within {
              border-color: var(--input-focus-border-color);
            }
          }
        `
      : css`
          .${ADVANCED_ACCOUNTING_CLASS_NAME} & input {
            border-color: var(--input-advanced-highlighted-border-color);

            :focus {
              border-color: var(--input-focus-border-color);
            }
          }
        `)};
`

const StyledInput = styled.input<{
  $dense?: boolean
  $error?: boolean
  $hasAdornment?: boolean
}>`
  font: inherit;
  color: inherit;
  flex: 1 1 auto;
  min-width: 0;
  text-align: inherit;
  color: ${({ theme }) => theme.colors.field.value};

  :focus {
    outline: none;
  }
  :disabled {
    color: ${({ theme }) => theme.colors.field.disabledValue};
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.field.placeholder};
  }

  :disabled::placeholder {
    color: ${({ theme }) => theme.colors.field.disabledPlaceholder};
  }

  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    background-color 0.2s;
`

const Adornment = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, dense = false, error = false, highlighted = false, id, startAdornment, endAdornment, ...rest },
  ref
) {
  const inputId = id || __uniqueId('input-')
  const hasAdornment = Boolean(startAdornment || endAdornment)

  return (
    <InputWrapper
      className={className}
      $dense={dense}
      $disabled={rest.disabled}
      $error={error}
      $hasAdornment={hasAdornment}
      $highlighted={highlighted}
    >
      {startAdornment && <Adornment>{startAdornment}</Adornment>}
      <StyledInput
        ref={ref as React.Ref<HTMLInputElement>}
        id={inputId}
        $dense={dense}
        $error={error}
        $hasAdornment={hasAdornment}
        {...rest}
      />
      {endAdornment && <Adornment>{endAdornment}</Adornment>}
    </InputWrapper>
  )
})

Input.displayName = 'Input'
